import React from 'react';
import { FocusLayerSizeV2, ShimBackgroundTypeV2 } from '@volkswagen-onehub/layer-manager';
import { PortalContext } from '../../../../context/portal-context';
import { BenefitsCombined } from '../../benefits';
import { BenefitsPopupLayerContent } from './benefits-popup-layer-content';
import { useBenefitsTracker } from '../hooks/use-benefits-tracker';

// hook para manejar el popup/layer de vouchers y promociones
export const useBenefitsPopupLayer = () => {
	const portalContext = React.useContext(PortalContext);
	const userData = portalContext.portalService.getPerson().getValue();
	const { layerManager } = portalContext;

	const [benefit, setBenefit] = React.useState<BenefitsCombined | undefined>(undefined);
	const [isLayerOpen, setIsLayerOpen] = React.useState(false);

	const { trackBenefitsLayerCloseClick, trackBenefitsLayerLoad } = useBenefitsTracker();

	const handleLayerOpen = (selectedBenefit: BenefitsCombined) => {
		setBenefit(selectedBenefit);

		if (!(selectedBenefit.type === 'promotion' && !selectedBenefit.data.contestant?.status)) {
			trackBenefitsLayerLoad(selectedBenefit);
		}

		setIsLayerOpen(true);
	};

	const handleLayerClose = React.useCallback(() => {
		setIsLayerOpen(false);
		trackBenefitsLayerCloseClick(benefit);
	}, [trackBenefitsLayerCloseClick, benefit]);

	const latestRef = React.useRef({ benefit, userData, portalContext, handleLayerClose });
	latestRef.current = { benefit, userData, portalContext, handleLayerClose };

	React.useEffect(() => {
		if (!isLayerOpen) return;

		const layer = layerManager.openFocusLayer(
			() => (
				<BenefitsPopupLayerContent
					benefit={latestRef.current.benefit}
					userData={latestRef.current.userData}
					portalContext={latestRef.current.portalContext}
					handleClose={() => latestRef.current.handleLayerClose()}
				/>
			),
			{},
			{
				size: FocusLayerSizeV2.A,
				shimBackgroundType: ShimBackgroundTypeV2.OPAQUE,
			},
		);

		return () => {
			layer.close();
		};

		// solo se abre/cierra por isLayerOpen; benefit/userData/portalContext/handleLayerClose se leen del ref para no forzar un remount del layer
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isLayerOpen, layerManager]);

	return { handleLayerOpen };
};
